// src/components/UI.jsx  — Shared design-system components
import React from "react";

/** Plain text from values that may be stored as { key, label } in Firestore. */
export function coercePlainText(v) {
  if (v == null || v === "") return "";
  if (React.isValidElement(v)) return "";
  if (typeof v === "object") {
    if (v.label != null) return String(v.label);
    if (v.key != null) return String(v.key);
    return "";
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return String(v);
}

/** Safe table/stat/detail cell: React elements pass through; objects become text. */
export function renderCellContent(v) {
  if (v == null || v === "") return "—";
  if (React.isValidElement(v)) return v;
  if (typeof v === "number" || typeof v === "boolean") return v;
  if (typeof v === "string") return v;
  if (typeof v === "object") return coercePlainText(v) || "—";
  return String(v);
}

// ── BUTTONS ──────────────────────────────────────────────────
export function Btn({ children, variant = "default", size = "md", onClick, disabled, type = "button", fullWidth, ...rest }) {
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
    fontFamily: "'Jost', sans-serif", fontWeight: 500, letterSpacing: "0.15em",
    textTransform: "uppercase", border: "1px solid", cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1, transition: "all 0.15s", borderRadius: 2,
    width: fullWidth ? "100%" : undefined,
  };
  const sizes = {
    sm: { fontSize: 10, padding: "7px 14px" },
    md: { fontSize: 11, padding: "11px 20px" },
    lg: { fontSize: 12, padding: "14px 28px" },
  };
  const variants = {
    default: { background: "transparent", color: "var(--slate)", borderColor: "var(--border)" },
    primary: { background: "var(--taupe)", color: "#FFFFFF", borderColor: "var(--taupe)" },
    gold:    { background: "var(--gold)", color: "#fff", borderColor: "var(--gold)" },
    danger:  { background: "var(--red)", color: "#fff", borderColor: "var(--red)" },
    ghost:   { background: "transparent", color: "var(--mid)", borderColor: "transparent" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...sizes[size], ...variants[variant] }}
      {...rest}>
      {children}
    </button>
  );
}

// ── BADGE ─────────────────────────────────────────────────────
const badgeColors = {
  active:     { bg: "var(--green-light)", color: "var(--green)" },
  paid:       { bg: "var(--green-light)", color: "var(--green)" },
  delivered:  { bg: "var(--green-light)", color: "var(--green)" },
  pending:    { bg: "var(--gold-pale)",   color: "#8A6830"       },
  pending_approval: { bg: "var(--gold-pale)", color: "#8A6830" },
  rejected:   { bg: "var(--red-light)",   color: "var(--red)"    },
  processing: { bg: "#EBF0F5",            color: "#2E4A6A"       },
  new:        { bg: "var(--gold-pale)",   color: "#8A6830"       },
  inactive:   { bg: "#F0F0EE",            color: "var(--mid)"    },
  cancelled:  { bg: "var(--red-light)",   color: "var(--red)"    },
  Partner:    { bg: "#F0F0EE",            color: "var(--mid)"    },
  Preferred:  { bg: "var(--gold-pale)",   color: "#8A6830"       },
  Principal:  { bg: "var(--charcoal)",    color: "var(--gold)"   },
};

/** For order status chips: maps internal status → display label; use with Badge. */
export function orderStatusForBadge(status) {
  if (status == null || status === "") return { key: "—", label: "—" };
  const s = String(status);
  const labels = {
    pending_approval: "Pending approval",
    rejected: "Rejected",
    new: "New",
    processing: "Processing",
    delivered: "Delivered",
    paid: "Paid",
    cancelled: "Cancelled",
  };
  return { key: s, label: labels[s] || s };
}

export function Badge({ label }) {
  if (React.isValidElement(label)) {
    return (
      <span style={{
        display: "inline-block", fontSize: 9, fontWeight: 500,
        letterSpacing: "0.1em", textTransform: "uppercase",
        padding: "3px 8px", borderRadius: 2,
        background: badgeColors.new.bg, color: badgeColors.new.color,
      }}>{label}</span>
    );
  }
  const displayText =
    label != null && typeof label === "object"
      ? coercePlainText(label) || "—"
      : label == null || label === ""
        ? "—"
        : String(label);
  const colorKey =
    label != null && typeof label === "object" && !React.isValidElement(label) && label.key != null
      ? label.key
      : displayText;
  const c = badgeColors[colorKey] || badgeColors[displayText] || badgeColors.new;
  return (
    <span style={{
      display: "inline-block", fontSize: 9, fontWeight: 500,
      letterSpacing: "0.1em", textTransform: "uppercase",
      padding: "3px 8px", borderRadius: 2,
      background: c.bg, color: c.color,
    }}>{displayText}</span>
  );
}

/** Commission rate digits + % in sans/tabular numerals so “1” is not mistaken for “I” in serif. */
export function CommissionPctFigures({ rate, color = "var(--gold)", fontSize = 38 }) {
  const n = Number(rate);
  const text = Number.isFinite(n) ? String(n) : String(rate ?? "");
  return (
    <span style={{
      fontFamily: "'Jost', sans-serif",
      fontWeight: 500,
      fontVariantNumeric: "tabular-nums",
      fontSize,
      color,
      lineHeight: 1,
      letterSpacing: "0.03em",
    }}>
      {text}
      <span style={{ marginLeft: "0.14em", fontWeight: 400 }}>%</span>
    </span>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────
export function StatCard({ label, value, delta, deltaPos, accent, hint, valueStyle }) {
  return (
    <div style={{
      background: "var(--warm-white)", border: "1px solid var(--border)",
      borderRadius: 4, padding: "22px 20px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--mid)", marginBottom: 10 }}>{label}</div>
      <div style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 38,
        fontWeight: 300,
        lineHeight: 1,
        color: accent ? "var(--gold)" : "var(--charcoal)",
        ...valueStyle,
      }}>{renderCellContent(value)}</div>
      {hint && <div style={{ fontSize: 11, color: "var(--mid)", marginTop: 10, fontWeight: 300, lineHeight: 1.45 }}>{hint}</div>}
      {delta && <div style={{ fontSize: 11, color: deltaPos ? "var(--green)" : "var(--red)", marginTop: 8 }}>{delta}</div>}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "var(--gold)", transform: "scaleX(0)", transformOrigin: "left", transition: "transform 0.3s" }} className="card-accent-line" />
    </div>
  );
}

// ── TABLE CARD ────────────────────────────────────────────────
export function TableCard({ title, actions, children }) {
  return (
    <div style={{ background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden", marginBottom: 24 }}>
      {(title || actions) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, padding: "18px 18px 14px", borderBottom: "1px solid var(--border)" }}>
          {title && <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 400 }}>{title}</div>}
          {actions && <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

// ── TABLE ─────────────────────────────────────────────────────
function tableColHeader(c) {
  if (c == null) return "";
  if (typeof c !== "object" || Array.isArray(c)) return String(c);
  // Use ?? so an intentional empty label (e.g. actions column) does not fall through to `c`
  return c.label ?? (c.key != null ? String(c.key) : "");
}

export function Table({ cols, rows, emptyMsg = "No records found." }) {
  const thStyle = { fontSize: 9, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--mid)", padding: "10px 20px", textAlign: "left", borderBottom: "1px solid var(--border)", background: "rgba(245,240,232,0.5)", whiteSpace: "nowrap" };
  const tdStyle = { padding: "13px 20px", fontSize: 13, fontWeight: 300, borderBottom: "1px solid var(--border)", verticalAlign: "middle" };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>{cols.map(c => <th key={c.key ?? c} style={thStyle}>{tableColHeader(c)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={cols.length} style={{ ...tdStyle, textAlign: "center", color: "var(--mid)", padding: "32px 20px" }}>{emptyMsg}</td></tr>
            : rows.map((row, i) => (
              <tr key={i} style={{ transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(245,240,232,0.5)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                {cols.map(c => <td key={c.key ?? c} style={tdStyle}>{renderCellContent(row[c.key ?? c])}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// ── MODAL ─────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(28,28,26,0.65)",
      backdropFilter: "blur(3px)", zIndex: 200, display: "flex",
      alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--warm-white)", width: "100%", maxWidth: 540,
        borderRadius: 4, overflow: "hidden", animation: "slideUp 0.2s ease",
      }}>
        <style>{`@keyframes slideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
        <div style={{ padding: "22px 26px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 400 }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: "var(--mid)", lineHeight: 1, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: "22px 26px" }}>{children}</div>
        {footer && <div style={{ padding: "14px 26px 22px", display: "flex", gap: 10, justifyContent: "flex-end", borderTop: "1px solid var(--border)" }}>{footer}</div>}
      </div>
    </div>
  );
}

// ── FORM FIELD ────────────────────────────────────────────────
export function Field({ label, children, half, required, hint }) {
  return (
    <div style={{ marginBottom: 16, flex: half ? "1 1 260px" : "1 1 100%" }}>
      <label style={{ display: "block", fontSize: 10, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--mid)", marginBottom: 6 }}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {hint ? <div style={{ fontSize: 11, color: "var(--mid)", marginTop: 6, fontWeight: 300, lineHeight: 1.45 }}>{hint}</div> : null}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "11px 13px",
  border: "1px solid var(--border)", borderRadius: 2,
  background: "var(--warm-white)", fontSize: 14, fontWeight: 300,
  color: "var(--charcoal)", outline: "none", transition: "border-color 0.2s",
};

export function Input(props) {
  return <input style={inputStyle} onFocus={e => e.target.style.borderColor = "var(--gold)"} onBlur={e => e.target.style.borderColor = "var(--border)"} {...props} />;
}

export function Select({ children, ...props }) {
  return <select style={{ ...inputStyle, appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%237A7A72'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }} {...props}>{children}</select>;
}

// ── ALERT TOAST ───────────────────────────────────────────────
export function Toast({ msg, type = "success", onClose }) {
  if (!msg) return null;
  const colors = { success: { bg: "var(--green-light)", border: "rgba(46,92,62,0.2)", color: "var(--green)" }, error: { bg: "var(--red-light)", border: "rgba(140,48,48,0.2)", color: "var(--red)" } };
  const c = colors[type] || colors.success;
  return (
    <div style={{
      position: "fixed", top: 24, right: 24, zIndex: 300,
      background: c.bg, border: `1px solid ${c.border}`, color: c.color,
      padding: "13px 18px", borderRadius: 4, fontSize: 13,
      display: "flex", alignItems: "center", gap: 14, boxShadow: "var(--shadow)",
      animation: "slideUp 0.2s ease", maxWidth: "min(92vw, 460px)",
    }}>
      {msg}
      <button onClick={onClose} style={{ background: "none", border: "none", color: "inherit", fontSize: 16, lineHeight: 1, cursor: "pointer" }}>×</button>
    </div>
  );
}

// ── SECTION LABEL ─────────────────────────────────────────────
export function SectionLabel({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, marginTop: 28 }}>
      <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--mid)", whiteSpace: "nowrap" }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

// ── LOADING SPINNER ───────────────────────────────────────────
export function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
      <div style={{ width: 32, height: 32, border: "2px solid var(--border)", borderTopColor: "var(--gold)", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── PROGRESS BAR ──────────────────────────────────────────────
export function ProgressBar({ value, max, label }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div>
      <div style={{ height: 4, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "var(--gold)", borderRadius: 2, transition: "width 0.6s ease" }} />
      </div>
      {label != null && label !== "" && <div style={{ fontSize: 10, color: "var(--mid)", marginTop: 4 }}>{renderCellContent(label)}</div>}
    </div>
  );
}

// ── DETAIL CARD ───────────────────────────────────────────────
export function DetailCard({ title, rows }) {
  return (
    <div style={{ background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: 4, padding: "20px 22px", marginBottom: 20 }}>
      {title && <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 400, marginBottom: 14 }}>{title}</div>}
      {rows.map(([k, v], i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "4px 10px", padding: "9px 0", borderBottom: i < rows.length - 1 ? "1px solid var(--border)" : "none", fontSize: 13 }}>
          <span style={{ color: "var(--mid)", fontWeight: 300, fontSize: 12 }}>{k}</span>
          <span style={{ fontWeight: 400 }}>{renderCellContent(v)}</span>
        </div>
      ))}
    </div>
  );
}

// ── AVATAR ────────────────────────────────────────────────────
export function Avatar({ name, size = 30 }) {
  const initials = name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "var(--charcoal)", color: "var(--gold)", fontFamily: "'Cormorant Garamond',serif", fontSize: size * 0.42, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {initials}
    </div>
  );
}

export function fmt$(n) {
  if (!n && n !== 0) return "—";
  return "$" + Number(n).toLocaleString();
}

export function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
